import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSupportWidget } from '../../contexts/SupportWidgetContext';
import { getWhatsAppUrl, CONTACT_PATH, HELP_CENTER_PATH } from '../../constants/siteNav';

const ContactUsFab = () => {
  const { openEmailWidget } = useSupportWidget();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleEmail = () => {
    setMenuOpen(false);
    openEmailWidget();
  };

  const fab = (
    <div
      ref={wrapRef}
      className="fixed bottom-5 right-4 sm:bottom-6 sm:right-6 z-[500] flex flex-col items-end gap-2.5"
    >
      {menuOpen && (
        <div
          className="bg-white rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.14)] border border-gray-200/90 overflow-hidden w-[min(calc(100vw-2rem),220px)] animate-slide-up"
          role="menu"
          aria-label="Contact options"
        >
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
            <p className="text-xs font-semibold text-gray-900">How would you like to reach us?</p>
          </div>
          <div className="p-2">
            <button
              type="button"
              role="menuitem"
              onClick={handleEmail}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
            >
              <span className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center shrink-0 group-hover:bg-gray-800 transition-colors">
                <i className="fas fa-envelope text-white text-sm" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">Email us</span>
                <span className="block text-[11px] text-gray-500">Usually within minutes to an hour</span>
              </span>
            </button>
            <a
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
            >
              <span className="w-9 h-9 rounded-lg bg-[#25D366] flex items-center justify-center shrink-0">
                <i className="fab fa-whatsapp text-white text-base" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">WhatsApp</span>
                <span className="block text-[11px] text-gray-500">Chat · fastest response</span>
              </span>
            </a>
            <button
              type="button"
              role="menuitem"
              onClick={() => go(HELP_CENTER_PATH)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
            >
              <span className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <i className="fas fa-book-open text-amber-700 text-sm" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">Help center</span>
                <span className="block text-[11px] text-gray-500">Guides · FAQs · tips</span>
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go(CONTACT_PATH)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
            >
              <span className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                <i className="fas fa-life-ring text-orange-600 text-sm" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">Contact page</span>
                <span className="block text-[11px] text-gray-500">All support options</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile / tablet: compact circular FAB · Desktop: labeled pill */}
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="lg:hidden w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-[0_6px_20px_rgba(0,0,0,0.22)] hover:bg-gray-800 active:scale-95 transition-all"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label="Contact us"
      >
        <i className={`fas ${menuOpen ? 'fa-times' : 'fa-comment-dots'} text-base`} />
      </button>

      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="hidden lg:flex items-center gap-2.5 bg-white text-gray-900 pl-4 pr-5 py-3 rounded-full border-2 border-gray-900/90 shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.16)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label="Contact us"
      >
        <span className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
          <i className="fas fa-comment-dots text-white text-sm" />
        </span>
        <span className="text-sm font-bold tracking-tight">Contact Us</span>
      </button>
    </div>
  );

  return createPortal(fab, document.body);
};

export default ContactUsFab;
